/**
 * Eagerly load the self-hosted display fonts (AmericanCaptain, BaronessKuffner)
 * via the FontFace API and register them on document.fonts.
 *
 * Why: these are large self-hosted .ttf files loaded by CSS @font-face with
 * font-display: swap. On mobile / flaky connections the lazy CSS-triggered fetch
 * can be slow or dropped, leaving text stuck in the Poppins fallback ("the font
 * doesn't load"). The Google/CDN header fonts don't have this problem because
 * they're actively fetched and well-cached. This brings the local fonts up to the
 * same reliability: we kick off the download immediately and RETRY on failure,
 * then add the loaded face so the browser repaints text with it.
 *
 * Mirrors the FontFace approach already used in ShareProfileModal/AdCreativeStudio
 * for canvas rendering, but applied app-wide at boot.
 */
const LOCAL_FONTS: Array<{ family: string; url: string }> = [
  { family: 'AmericanCaptain', url: '/fonts/AmericanCaptain.ttf' },
  { family: 'BaronessKuffner', url: '/fonts/BaronessKuffner.ttf' },
];

const MAX_ATTEMPTS = 4;

function loadOne({ family, url }: { family: string; url: string }, attempt = 0): void {
  try {
    const face = new FontFace(family, `url("${url}") format("truetype")`, {
      style: 'normal',
      weight: 'normal',
      display: 'swap',
    });
    face
      .load()
      .then((loaded) => {
        try { (document.fonts as FontFaceSet).add(loaded); } catch (_) {}
      })
      .catch(() => {
        // Retry with backoff — transient mobile network drops shouldn't leave
        // the font permanently unloaded.
        if (attempt + 1 < MAX_ATTEMPTS) {
          setTimeout(() => loadOne({ family, url }, attempt + 1), 1000 * (attempt + 1));
        }
      });
  } catch (_) {
    // FontFace constructor unsupported — CSS @font-face fallback still applies.
  }
}

export function loadLocalFonts(): void {
  if (typeof document === 'undefined' || typeof (window as any).FontFace === 'undefined' || !(document as any).fonts) {
    return;
  }
  LOCAL_FONTS.forEach((f) => loadOne(f));
}

export default loadLocalFonts;
